// Toggle — boolean switch with smooth slide animation.
//
// Teal track when on, Stone (text-tertiary) track when off. The thumb is
// raised paper-white and slides left/right via translate utilities;
// transition-base sources the standard token timing.
//
// Accessible: role="switch" + aria-checked. Pair with a label by passing
// the `label` prop, or compose externally and use `aria-labelledby`.

import { forwardRef, useId, type ButtonHTMLAttributes, type ReactNode } from "react";

import { cn } from "./cn";

export interface ToggleProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "onChange" | "type"> {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: ReactNode;
  disabled?: boolean;
}

export const Toggle = forwardRef<HTMLButtonElement, ToggleProps>(function Toggle(
  { checked, onChange, label, disabled = false, className, id, ...rest },
  ref,
) {
  const generatedId = useId();
  const labelId = id ?? generatedId;

  const handleClick = () => {
    if (disabled) return;
    onChange(!checked);
  };

  const button = (
    <button
      ref={ref}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-labelledby={label ? `${labelId}-label` : undefined}
      onClick={handleClick}
      disabled={disabled}
      data-checked={checked ? "true" : "false"}
      className={cn(
        "relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full",
        "transition-base ease-out outline-none focus-visible:shadow-focus",
        "disabled:cursor-not-allowed disabled:opacity-50",
        checked ? "bg-teal" : "bg-text-tertiary",
        className,
      )}
      {...rest}
    >
      <span
        aria-hidden="true"
        className={cn(
          "inline-block h-4 w-4 rounded-full bg-raised transition-base ease-out shadow-hover",
          checked ? "translate-x-4" : "translate-x-0.5",
        )}
      />
    </button>
  );

  if (!label) return button;

  return (
    <label className="inline-flex items-center gap-2 cursor-pointer">
      {button}
      <span id={`${labelId}-label`} className="text-sm text-text-primary">
        {label}
      </span>
    </label>
  );
});
